/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input, ViewChild } from '@angular/core';
import { type ValidationErrors, type ValidatorFn, Validators } from '@angular/forms';
import {
type ColumnDef,
type Command, type HttpResponse, type TableConfig, BatchService, ButtonService,
DataTableComponent, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract, UNPLACEHOLDER } from '@dm/core';
import { type Button } from '@dm/core/types';
import { ToolService } from 'app/common/services';
import { type ServiceLDAP, type ServiceLDAPAdvanced } from 'app/common/types/mo';
import { SWITCH } from 'app/config';
import { CA_STATUS, CaUploadComponent } from 'app/feature/settings/ca-upload/ca-upload.component';
import { CaUploadService } from 'app/feature/settings/ca-upload/ca-upload.service';
import { cloneDeep } from 'lodash';
import { type Observable, type Subscription, forkJoin, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

import { ServiceLdapService } from '../service-ldap.service';
import { type ServiceAddress, BindTypeE, ProtocalE, ScopeE, ServiceLdapStrService } from '../service-ldap-str.service';

@Component({
    selector: 'app-configure-service-ldap',
    templateUrl: './configure-service-ldap.component.html',
    styleUrls: ['./configure-service-ldap.component.scss'],
})
export class ConfigureServiceLdapComponent extends FormSlideWindowAbstract implements OnInit {
    /**
     * 是否展示极简样式
     */
    @Input() simpleModeStyle = false;
    @Input() data: ServiceLDAP;
    @Input() vstoreId: string;

    addBtn: Button;
    removeBtn: Button;
    testBtn: Button;

    advancedData: ServiceLDAPAdvanced;
    advancedTemplate: { [key: string]: ServiceLDAPAdvanced } = {};

    i18n = this.str.i18n;
    ProtocalE = ProtocalE;
    BindTypeE = BindTypeE;
    ScopeE = ScopeE;
    UNPLACEHOLDER = UNPLACEHOLDER;
    SWITCH = SWITCH;

    loadFailed = false;
    loading = false;

    vm: FormGroup;
    advancedVm: FormGroup;
    errorMessage: any;
    advanced = false;

    windowVisible = true;
    pageReady = true;

    // IP或域名校验器
    ipOrDomainValidator: ValidatorFn = DMValidators.or([DMValidators.ipWithMulticast, DMValidators.strictDomain]);

    certificateType = 2;
    caStatus: CA_STATUS;
    @ViewChild('caUploadEl') caUploadEl: CaUploadComponent;
    @ViewChild('dt') dt: DataTableComponent;

    /** 单个服务器地址最小长度为1 */
    SERVER_ADDRESS_MIN_LENGTH = 1;
    /** 单个服务器地址最大长度为255 */
    SERVER_ADDRESS_MAX_LENGTH = 255;
    /** 服务器IP地址表格 */
    tableData = [];
    /** 表格临时数据，用于在行编辑取消时，恢复数据 */
    tempTableData = [];
    tableConfig: TableConfig;
    columnDefs: ColumnDef[];
    selection = [];
    /** 服务器地址数组 */
    serverAddressArr: string[] = [];
    /** 服务器地址虚拟id */
    serverAddressId = 0;
    /** 服务器地址必填提示 */
    serverAddressRequiredTip = this.toolService.requiredErr(this.i18n.serverAddress);
    /** 服务器地址添加按钮灰化提示 */
    serverAddressDisabledTip = this.i18n.serverAddressDisabledTip;
    /** 判断表格数据是否为空 */
    isAddressEmpty = false;

    isSimpleMode: boolean;

    /** 服务器地址最多64个 */
    get addBtnDisabled(): boolean {
        return this.dt?.count >= 64;
    }

    constructor(
        injector: Injector,
        public str: ServiceLdapStrService,
        private service: ServiceLdapService,
        private toolService: ToolService,
        private caUploadService: CaUploadService,
        private batchService: BatchService,
        private buttonService: ButtonService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.isSimpleMode = SWITCH.IS_SIMPLE_MODE || this.simpleModeStyle;
        this.initVstoreId();
        this.initData();
    }

    initVstoreId(): string {
        return this.data ? this.data.vstoreId : this.vstoreId;
    }

    refresh(): void {
        this.initData();
    }

    /**
     * 初始化按钮
     */
    initBtns(): void {
        this.removeBtn = this.buttonService.create({
            id: 'file_ldap_delete_ipAddress_btn',
            label: this.i18n.removeBtn,
            disabledCheck: () => !this.selection.length,
            command: () => {
                this.remove();
            },
        });

        this.testBtn = this.buttonService.create({
            id: 'file_ldap_test_ipAddress_btn',
            label: this.i18n.testBtn,
            disabledCheck: () => !this.selection.length,
            command: () => {
                this.test(this.selection);
            },
        });
    }

    initTable(): void {
        this.tableConfig = {
            selectionMode: 'checkbox',
            dataKey: 'id',
            motion: true,
        };
        this.columnDefs = [
            {
                header: this.i18n.serverAddress,
                field: 'serverAddress',
                helpContent: this.i18n.serverAddressTip,
                filterType: 'search',
            },
            {
                header: this.i18n.operationBtn,
                field: 'buttons',
                width: 90,
                minWidth: 80,
            },
        ];
    }

    initData(): Subscription {
        this.loading = true;
        this.loadFailed = false;

        return forkJoin([
            this.getData(),
            this.getAdvancedData(),
        ]).subscribe(() => {
            this.initVm();
            this.initAdvancedVm();
            this.initErrorMessage();
            this.getTemplate();

            // 将接口数据处理为表格数据
            this.serverAddressArr = this.getAddressArr(this.data.LDAPSERVER);
            this.tableData = this.getTableData(this.serverAddressArr);
            this.tempTableData = cloneDeep(this.tableData);
            this.initTable();
            this.initBtns();

            // 首次配置时，先添加一条服务器地址
            if (!this.tableData.length) {
                this.add();
            }

            this.loading = false;
            this.loadFailed = false;
        }, () => {
            this.loading = false;
            this.loadFailed = true;
        });
    }

    getData(): Observable<any> {
        if (this.data) {
            return of(this.data);
        }

        return this.service.get(this.initVstoreId()).pipe(
            tap(({ data }) => {
                this.data = data;
            }),
        );
    }

    getAddressArr(ldapServer: string): string[] {
        if (!ldapServer) {
            return [];
        }

        return ldapServer.split(',');
    }

    /**
     * 生成表格数据
     */
    getTableData(ipDatas: string[]): ServiceAddress[] {
        if (!ipDatas) {
            return [];
        }

        const tableDatas = [];
        ipDatas.forEach((item: string) => {
            this.serverAddressId++;
            const params = {
                id: this.serverAddressId,
                serverAddress: item,
                editStatus: false,
            };

            tableDatas.push(params);
        });

        return tableDatas;
    }

    /**
     * 查询高级配置信息
     */
    getAdvancedData(): Observable<HttpResponse> {
        return this.service.getAdvanced(this.initVstoreId(), null)
            .pipe(
                tap(({ data }) => {
                    this.advancedData = data;
                }),
            );
    }

    /**
     * 获取CA证书状态
     */
    getCaStatus(): Observable<any> {
        return this.caUploadService.getInfo(this.certificateType, { mask: true })
            .pipe(
                tap((data) => {
                    this.caStatus = data ? data.CA_CERTIFICATE_STATUS : CA_STATUS.NOT_EXIST;
                }),
            );
    }

    /**
     * 查询高级配置的模板
     */
    getTemplate(): void {
        [1, 2].forEach((template) =>
            this.service.getAdvanced(this.data.vstoreId, template)
                .subscribe(({ data }) => {
                    if (template) {
                        this.advancedTemplate[template] = data;
                    }
                }),
        );
    }

    /**
     * 初始化表单
     */
    initVm(): void {
        const data = this.data;

        this.vm = new FormGroup({
            PORTNUM: new FormControl(data.PORTNUM || 636),
            TRANSFERTYPE: new FormControl(data.TRANSFERTYPE ? Number(data.TRANSFERTYPE) : ProtocalE.LDAPS),
            BASEDN: new FormControl(data.BASEDN || '', [
                Validators.required,
                DMValidators.rangeByteLength(1, 1024),
                this.dnValidator,
            ]),
            BINDUSINGADCRED: new FormControl(data.BINDUSINGADCRED === 'true'),
            BINDLEVEL: new FormControl(data.BINDLEVEL ? Number(data.BINDLEVEL) : BindTypeE.Simple),
            USERNAME: new FormControl(data.USERNAME || '', [
                this.bindDnRequiredValidator('PASSWORD'),
                DMValidators.rangeByteLength(0, 1024),
                this.dnValidator,
            ]),
            PASSWORD: new FormControl('', [
                this.bindDnRequiredValidator('USERNAME'),
                this.passwordRequiredValidator,
                DMValidators.rangeByteLength(0, 63),
            ]),
            CONFIRMPASSWORD: new FormControl('', [
                this.confirmPasswordValidator,
            ]),
            USERSUFFIX: new FormControl(data.USERSUFFIX || '', [
                DMValidators.rangeByteLength(0, 1024),
                this.dnValidator,
            ]),
            USERSCOPE: new FormControl(data.USERSCOPE ? Number(data.USERSCOPE) : ScopeE.SubTree),
            GROUPSUFFIX: new FormControl(data.GROUPSUFFIX || '', [
                DMValidators.rangeByteLength(0, 1024),
                this.dnValidator,
            ]),
            GROUPSCOPE: new FormControl(data.GROUPSCOPE ? Number(data.GROUPSCOPE) : ScopeE.SubTree),
            NETGROUPDN: new FormControl(data.NETGROUPDN || '', [
                DMValidators.rangeByteLength(0, 1024),
                this.dnValidator,
            ]),
            NETGROUPSCOPE: new FormControl(data.NETGROUPSCOPE ? Number(data.NETGROUPSCOPE) : ScopeE.SubTree),
            TIMELIMIT: new FormControl(data.TIMELIMIT || 3),
            BINDTIMELIMIT: new FormControl(data.BINDTIMELIMIT || 3),
            IDLETIMELIMIT: new FormControl(data.IDLETIMELIMIT || 3),
        }, {
            validators: [
                DMValidators.equal(['PASSWORD', 'CONFIRMPASSWORD']),
            ],
            updateOn: 'blur',
        });

        this.vm.get('USERNAME').valueChanges.subscribe((value) => {
            if (value !== this.vm.value.USERNAME) {
                this.vm.get('PASSWORD').updateValueAndValidity({ onlySelf: false, emitEvent: true });
            }
        });
        this.vm.get('PASSWORD').valueChanges.subscribe((value) => {
            if (value !== this.vm.value.PASSWORD) {
                this.vm.get('USERNAME').updateValueAndValidity({ onlySelf: false, emitEvent: true });
                this.vm.get('CONFIRMPASSWORD').updateValueAndValidity({ onlySelf: false, emitEvent: true });
            }
        });
    }

    /**
     * 初始化高级表单
     */
    initAdvancedVm(): void {
        const data = this.advancedData;

        this.advancedVm = new FormGroup({
            template: new FormControl(null),
            POSIXACCOUNTOBJECTCLASS: new FormControl(data.POSIXACCOUNTOBJECTCLASS || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            POSIXGROUPOBJECTCLASS: new FormControl(data.POSIXGROUPOBJECTCLASS || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            NISNETGROUPOBJECTCLASS: new FormControl(data.NISNETGROUPOBJECTCLASS || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            UIDATTRIBUTE: new FormControl(data.UIDATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            UIDNUMBERATTRIBUTE: new FormControl(data.UIDNUMBERATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            GIDNUMBERATTRIBUTE: new FormControl(data.GIDNUMBERATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            CNFORGROUPSATTRIBUTE: new FormControl(data.CNFORGROUPSATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            CNFORNETGROUPSATTRIBUTE: new FormControl(data.CNFORNETGROUPSATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            MEMBERUIDATTRIBUTE: new FormControl(data.MEMBERUIDATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            MEMBERNISNETGROUPATTRIBUTE: new FormControl(data.MEMBERNISNETGROUPATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            NISNETGROUPTRIPLEATTRIBUTE: new FormControl(data.NISNETGROUPTRIPLEATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            ENABLERFC2307BIS: new FormControl(data.ENABLERFC2307BIS === 'true'),
            GROUPOFUNIQUENAMESOBJECTCLASS: new FormControl(data.GROUPOFUNIQUENAMESOBJECTCLASS || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
            UNIQUEMEMBERATTRIBUTE: new FormControl(data.UNIQUEMEMBERATTRIBUTE || '', [
                DMValidators.rangeByteLength(0, 1024),
            ]),
        }, { updateOn: 'blur' });
    }

    /**
     * 初始化错误信息
     */
    initErrorMessage(): void {
        const i18n = this.i18n;

        this.errorMessage = {
            BASEDN: {
                required: this.toolService.requiredErr(i18n.baseDNLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(1, 1024),
                dn: i18n.dnErr(i18n.baseDNLab),
            },
            USERNAME: {
                required: this.toolService.requiredErr(i18n.bindDNLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
                dn: i18n.dnErr(i18n.bindDNLab),
            },
            PASSWORD: {
                required: this.toolService.requiredErr(i18n.bindPasswordLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 63),
                passwordRequired: i18n.passwordRequiredErr,
            },
            CONFIRMPASSWORD: {
                required: this.toolService.requiredErr(i18n.confirmBindPasswordLab),
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 63),
                equal: i18n.confirmPasswordErr,
            },
            USERSUFFIX: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
                dn: i18n.dnErr(i18n.userDirLab),
            },
            GROUPSUFFIX: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
                dn: i18n.dnErr(i18n.groupDirLab),
            },
            NETGROUPDN: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
                dn: i18n.dnErr(i18n.networkGroupDNLab),
            },
            POSIXACCOUNTOBJECTCLASS: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            POSIXGROUPOBJECTCLASS: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            NISNETGROUPOBJECTCLASS: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            UIDATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            UIDNUMBERATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            GIDNUMBERATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            CNFORGROUPSATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            CNFORNETGROUPSATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            MEMBERUIDATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            MEMBERNISNETGROUPATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            NISNETGROUPTRIPLEATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            GROUPOFUNIQUENAMESOBJECTCLASS: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
            UNIQUEMEMBERATTRIBUTE: {
                rangeByteLength: this.toolService.rangeByteLengthErr(0, 1024),
            },
        };
    }

    /**
     * DN格式校验。
     * 格式必须为k1=v1,k2=v2...
     */
    dnValidator = (control: FormControl): ValidationErrors | null => {
        const value: string = control.value;

        if (!value) {
            return null;
        }

        const arr = value.split(',');
        const valid = arr.every((item) => {
            const kv = item.split('=');
            if (kv.length !== 2) {
                return false;
            } else {
                // 键、值不能为空
                if (!kv[0] || !kv[1]) {
                    return false;
                }

                // 值首尾字符不能为空格
                if (kv[1][0] === ' ' || kv[1].slice(-1) === ' ') {
                    return false;
                }

                // 值首字母不能为#
                const reg = /^#.*$/;
                if (reg.test(kv[1])) {
                    return false;
                }
            }

            return true;
        });

        return valid ? null : { dn: true };
    };

    /**
     * 绑定DN、绑定密码必填校验。其中一个变化，另外一个必填
     */
    bindDnRequiredValidator(name: string): ValidatorFn {
        return (control: FormControl): ValidationErrors | null => {
            if (!this.vm) {
                return null;
            }

            const value: string = control.value;
            const otherControl = this.vm.get(name) as FormControl;

            return otherControl.valueChanged && otherControl.value && !value ? { required: true } : null;
        };
    }

    /**
     * 如果配置了绑定密码，修改LDAP服务器IP的同时需要输入绑定密码。
     */
    passwordRequiredValidator = (control: FormControl): ValidationErrors => {
        // 绑定DN和绑定密码是同步配置的，用绑定DN判断
        const unValid = this.vm?.controls.USERNAME.value && this.isDataTableModified() && !control.value;

        return unValid ? { passwordRequired: true } : null;
    };

    /**
     * 确认密码校验。密码输入后，确认密码就不能为空
     */
    confirmPasswordValidator = (control: FormControl): ValidationErrors | null => {
        if (!this.vm) {
            return null;
        }

        if (this.vm.get('PASSWORD').value && !control.value) {
            return { required: true };
        }
        return null;
    };

    /**
     * 协议切换，改变默认端口
     */
    onProtocalChange(value: number): void {
        const port = value === ProtocalE.LDAP ? 389 : 636;
        this.vm.patchValue({ PORTNUM: port });
    }

    /**
     * LDAP模板变化，将模板的值更新到表单
     * @param value
     */
    onTempalteChange(value: number): void {
        const template = this.advancedTemplate[value];

        if (template) {
            const data = {
                ...template,
                ENABLERFC2307BIS: template.ENABLERFC2307BIS === 'true',
            };

            this.advancedVm.patchValue(data);
        }
    }

    /**
     * 表格增加服务器IP地址
     */
    add(): void {
        this.serverAddressId++;
        const params = {
            id: this.serverAddressId,
            serverAddress: '',
            editStatus: true,
            isNewItem: true,
        };

        this.tableData.push(params);
        this.dt?.updateData(this.tableData);

        // 点击“增加”也需要更新isAddressEmpty的状态，避免清空table数据再增加数据，validate()校验不通过
        this.checkIpEmpty();
    }

    /**
     * 检查服务器IP地址是否为空
     */
    checkIpEmpty(): boolean {
        this.isAddressEmpty = this.tableData.length === 0;
        return this.isAddressEmpty;
    }

    /**
     * 批量移除服务器IP地址
     */
    remove(): void {
        this.selection.forEach((address: ServiceAddress) => {
            this.deleteServerAddress(address);
        });
    }

    /**
     * 测试服务器地址
     */
    test(serverAddress: ServiceAddress[]): void {
        const cmds: Command[] = [];
        const portNum = this.vm.get('PORTNUM').value;
        serverAddress.forEach((item) => {
            this.onBlurServerIpAddress(item);
            if (item.isInvalid) {
                return;
            }

            cmds.push({
                name: this.str.getTestIpAddress(item.serverAddress),
                action: this.service.test(this.data.vstoreId, item.serverAddress, portNum),
            });
        });

        if (cmds.length > 0) {
            this.batchService.exePrompt(cmds);
        }
    }

    /**
     * 表单是否改变过
     */
    isModified(): boolean {
        let changed = this.vm.rowValueChanged || this.advancedVm.rowValueChanged || this.isDataTableModified();

        if (!changed && this.caUploadEl) {
            changed = this.caUploadEl.changed;
        }

        return changed;
    }

    /**
     * 验证表格是否被修改过
     */
    isDataTableModified(): boolean {
        if (this.tableData.length !== this.serverAddressArr.length) {
            return true;
        }

        // 判断是否有与接口不一样的地址
        return this.tableData.some((item: ServiceAddress) => !this.serverAddressArr.includes(item.serverAddress));
    }

    /**
     * 服务器IP地址校验
     */
    onBlurServerIpAddress(data: ServiceAddress): void {
        // 长度校验
        if (!data.serverAddress.length || data.serverAddress.length > this.SERVER_ADDRESS_MAX_LENGTH) {
            data.isInvalid = true;
            data.validFailedTips = data.serverAddress.length ?
                this.toolService.rangeByteLengthErr(this.SERVER_ADDRESS_MIN_LENGTH, this.SERVER_ADDRESS_MAX_LENGTH) :
                this.toolService.requiredErr(this.i18n.serverAddress);
            return;
        }

        // IP地址重复校验
        const dataRepeat = this.tableData.some((value) =>
            value.serverAddress === data.serverAddress && value.id !== data.id,
        );
        if (dataRepeat) {
            data.isInvalid = true;
            data.validFailedTips = this.i18n.ipRepeatErr;
            return;
        }

        // IP或域名校验
        const addressControl = new FormControl(data.serverAddress);
        if (this.ipOrDomainValidator(addressControl)) {
            data.isInvalid = true;
            data.validFailedTips = this.i18n.ipErr;
            return;
        }

        data.isInvalid = false;
        data.validFailedTips = '';
    }

    /**
     * 表格内点击绿色√
     */
    confirmServerAddress(item: ServiceAddress): void {
        this.onBlurServerIpAddress(item);
        if (item.isInvalid) {
            return;
        }

        item.isNewItem = false;
        this.tempTableData = cloneDeep(this.tableData);
        item.editStatus = false;
    }

    /**
     * 取消表格行的修改
     */
    cancelModifyServerAddress(item: ServiceAddress): void {
        const tableItem = this.tableData.find((value) => value.id === item.id);

        // 如果是新增数据，未点击√，则将该数据从表格中删除
        if (tableItem?.isNewItem) {
            this.deleteServerAddress(item);
        } else {
            // 重置为修改前的数据
            const cloneItem = this.tempTableData.find((value) => value.id === item.id);
            item.serverAddress = cloneItem.serverAddress;
            item.editStatus = false;
        }
    }

    /**
     * 表格行项的修改按钮
     */
    modifyServerAddress(item: ServiceAddress): void {
        item.editStatus = true;
    }

    /**
     * 删除表格的数据
     */
    deleteServerAddress(item: ServiceAddress): void {
        this.tableData.splice(
            this.tableData.findIndex((value) => value.id === item.id),
            1,
        );
        this.dt.updateData(this.tableData);
        this.checkIpEmpty();
    }

    /**
     * 校验表单
     */
    validate(): Observable<boolean> {
        return forkJoin([
            this.vm.validateOb(),
            this.advancedVm.validateOb(),
            of(this.caUploadEl ? this.caUploadEl.validate() : true),
        ])
            .pipe(
                map((results) => {
                    // 对编辑状态的服务器地址进行校验
                    const dataTableValid = this.tableData.filter((item) => item.editStatus)
                        .every((item) => {
                            this.onBlurServerIpAddress(item);
                            return !item.isInvalid;
                        });

                    return !this.isAddressEmpty &&
                        dataTableValid &&
                        results.every((valid) => valid);
                }),
            );
    }

    /**
     * 构建下发命令
     */
    buildCmds(): Command[] {
        const cmds: Command[] = [];

        if (this.caUploadEl) {
            cmds.push(...this.caUploadEl.upload());
        }

        // 构建ip地址数据
        const ips: string[] = [];
        this.tableData.forEach((item) => {
            ips.push(item.serverAddress);
        });

        if (this.vm.rowValueChanged || this.isDataTableModified()) {
            const vmValue = this.vm.value;
            const params: ServiceLDAP = this.vm.changedRawValue || {};

            // 必须下发的参数
            params.LDAPSERVER = ips.join(',');
            params.PORTNUM = vmValue.PORTNUM;
            params.TRANSFERTYPE = vmValue.TRANSFERTYPE;
            params.BASEDN = vmValue.BASEDN;
            params.USERSUFFIX = vmValue.USERSUFFIX;
            params.GROUPSUFFIX = vmValue.GROUPSUFFIX;
            params.BINDUSINGADCRED = vmValue.BINDUSINGADCRED;

            if (params.USERNAME || params.PASSWORD) {
                params.USERNAME = vmValue.USERNAME;
                params.PASSWORD = vmValue.PASSWORD;
            }

            // 删除前端字段
            delete params.CONFIRMPASSWORD;

            cmds.push({
                name: this.i18n.modifyResult,
                action: this.service.modify(this.data.vstoreId, params),
            });
        }

        if (this.advancedVm.rowValueChanged) {
            const params: any = this.advancedVm.changedRawValue;

            if (params.template) {
                params.SCHEMATYPESTORE = params.template;
            }

            delete params.template;
            if (Object.keys(params).length > 0) {
                cmds.push({
                    name: this.i18n.modifyAdvancedResult,
                    action: this.service.modifyAdvanced(this.data.vstoreId, params),
                });
            }
        }

        return cmds;
    }
}
